import React, { useEffect, useReducer, useState } from 'react';
import { Button, Grid, Section, Select, TextInput, ToggleField } from '../../../common';
import { useAbstractConnectivityConfigurationForm } from './AbstractConnectivityForm';
import AWSAccountConnectivityConfiguration from '../../../../models/entities/AWSAccountConnectivityConfiguration';
import AWSAccountSubnet from '../../../../models/entities/AWSAccountSubnet';
import './AccountConnectivitySettingsModal.css';
import { isIpAddressInputValid } from '../../../common/form-controls/IpAddressValidator';
import { IValidation } from '../../../../utils/Validation';
import { useAbstractSubnetForm } from './subnets/AbstractSubnetForm';
import AWSAccountRequest from '../../../../models/entities/AWSAccountRequest';
import StandardModal from '../../../common/modals/StandardModal';

interface AccountConnectivitySettingsModalProps {
	visible: boolean;
	isReadOnly: boolean;
	setConnectivitySettingsModal: (e: boolean) => void;
	onConfirm: (model: AWSAccountConnectivityConfiguration) => void;
	editingConfiguration: AWSAccountConnectivityConfiguration;
	account: AWSAccountRequest;
	network: AWSAccountConnectivityConfiguration[];
}

interface Tag {
	key: string;
	value: string;
}

const toTagsArray = (obj: Record<string, string>): Tag[] => Object.entries(obj).map(([key, value]) => ({ key, value }));

const toTagsObject = (arr: Tag[]): Record<string, string> =>
	Object.fromEntries(arr.filter((t) => t.key).map((t) => [t.key, t.value]));

type TagsAction =
	| { type: 'init_from_object'; payload: Record<string, string> }
	| { type: 'add'; payload?: Tag }
	| { type: 'remove'; index: number }
	| { type: 'update'; index: number; field: 'key' | 'value'; value: string }
	| { type: 'reset' };

function tagsReducer(state: Tag[], action: TagsAction): Tag[] {
	switch (action.type) {
		case 'init_from_object':
			return toTagsArray(action.payload ?? {});
		case 'add':
			return [...state, action.payload ?? { key: '', value: '' }];
		case 'remove':
			return state.length <= 1 ? state : state.filter((_, i) => i !== action.index);
		case 'update':
			return state.map((t, i) => (i === action.index ? { ...t, [action.field]: action.value } : t));
		case 'reset':
			return [{ key: '', value: '' }];
		default:
			return state;
	}
}

const AccountConnectivitySettingsModal = ({
	isReadOnly,
	visible,
	setConnectivitySettingsModal,
	onConfirm,
	editingConfiguration,
	account,
	network,
}: AccountConnectivitySettingsModalProps) => {
	const { state, setState, formModel, getStateFormHandlers, getDefaultForm, isValid } =
		useAbstractConnectivityConfigurationForm(editingConfiguration);
	const stateFormHandler = () => getStateFormHandlers();
	const [tags, dispatchTags] = useReducer(tagsReducer, [{ key: '', value: '' }]);
	const { subnetStateForm, formSubnetModel, getSubnetStateFormHandlers, handleStateFormChange, isValidSubnet } =
		useAbstractSubnetForm();
	const subnetStateFormHandler = () => getSubnetStateFormHandlers();
	const [regionOptions, setRegionOptions] = useState([
		{ value: 'us-east-2', text: 'Ohio (us-east-2)' },
		{ value: 'eu-west-2', text: 'London (eu-west-2)' },
		{ value: 'ap-southeast-1', text: 'Singapore (ap-southeast-1)' },
		{ value: 'ap-south-1', text: 'Mumbai (ap-south-1)' },
		{ value: 'ap-southeast-5', text: 'Malaysia (ap-southeast-5)' },
		{ value: 'ap-southeast-4', text: 'Melbourne (ap-southeast-4)' },
		{ value: 'sa-east-1', text: 'Sao Paulo (sa-east-1)' },
	]);

	const isModalForNewRule = () => {
		return state.stateForm.editingConfiguration != null;
	};

	const isSubnetValid = () => {
		let isNameValid: boolean, isAZ1Valid: boolean, isAZ2Valid: boolean, isAZ3Valid: boolean;
		state.stateForm.subnets.map((subnet) => {
			isNameValid = subnet.name && subnet.name.trim() !== '';
			isAZ1Valid = isIpAddressInputValid(subnet.aZ1_CIDR, false, true);
			isAZ2Valid =
				subnet.aZ2_CIDR == null || subnet.aZ2_CIDR == '' || isIpAddressInputValid(subnet.aZ2_CIDR, false, true);
			isAZ3Valid =
				subnet.aZ3_CIDR == null || subnet.aZ3_CIDR == '' || isIpAddressInputValid(subnet.aZ3_CIDR, false, true);
		});

		return isNameValid && isAZ1Valid && isAZ2Valid && isAZ3Valid;
	};

	const getSubnetValidation = (subnet: AWSAccountSubnet, field: keyof AWSAccountSubnet): IValidation => {
		switch (field) {
			case 'name':
				return {
					required: true,
					rules: [
						{
							assert: () => subnet.name && subnet.name.trim() !== '',
							message: 'Name is required.',
						},
					],
				};
			case 'aZ1_CIDR':
				return {
					required: true,
					rules: [
						{
							assert: () => isIpAddressInputValid(subnet[field] as string, false, true),
							message: 'Not a valid IP address.',
						},
					],
				};
			case 'aZ2_CIDR':
				return {
					required: false,
					rules: [
						{
							assert: () =>
								!subnet.aZ2_CIDR ||
								isIpAddressInputValid(subnet.aZ2_CIDR, false, true),
							message: 'Not a valid IP address.',
						},
					],
				};

			case 'aZ3_CIDR':
				return {
					required: false,
					rules: [
						{
							assert: () =>
								!subnet.aZ3_CIDR ||
								isIpAddressInputValid(subnet.aZ3_CIDR, false, true),
							message: 'Not a valid IP address.',
						},
					],
				};
			case 'uniqueRouteTablePerAz':
				return {
					required: true,
					rules: [],
				};
			default:
				return { required: false, rules: [] };
		}
	};

	const handleRequest = async (e) => {
		const model = formModel.create(AWSAccountConnectivityConfiguration);
		onConfirm(model);
		setState({ stateForm: getDefaultForm() });
		dispatchTags({ type: 'reset' });
	};

	const addTag = () => {
		dispatchTags({ type: 'add' });
	};

	const removeTag = (index: number) => {
		dispatchTags({ type: 'remove', index });
	};

	const updateTag = (index: number, field: 'key' | 'value', value: string) => {
		dispatchTags({ type: 'update', index, field, value });
	};

	const addSubnets = () => {
		stateFormHandler().subnets.onChange([
			...state.stateForm.subnets,
			{ aZ1_CIDR: '', aZ2_CIDR: null, aZ3_CIDR: null, name: '', uniqueRouteTablePerAz: false },
		]);
	};

	const removeSubnet = (index: number) => {
		if (state.stateForm.subnets.length > 1) {
			const newSubnets = state.stateForm.subnets.filter((_, i) => i !== index);
			stateFormHandler().subnets.onChange(newSubnets);
		}
	};

	function updateSubnet<K extends keyof AWSAccountSubnet>(index: number, field: K, value: AWSAccountSubnet[K]) {
		const newSubnets = [...state.stateForm.subnets];
		newSubnets[index][field] = value;
		stateFormHandler().subnets.onChange(newSubnets);
		handleStateFormChange(field, value);
	}

	useEffect(() => {
		if (editingConfiguration?.tags) {
			dispatchTags({ type: 'init_from_object', payload: editingConfiguration.tags as Record<string, string> });
		}
	}, [editingConfiguration]);

	useEffect(() => {
		stateFormHandler().tags.onChange(toTagsObject(tags));
	}, [tags]);

	useEffect(() => {
		if (!isReadOnly && account?.regionNames && Array.isArray(account.regionNames)) {
			let regions = regionOptions.filter((r) => account.regionNames.includes(r.value));
			let regionsInNetwork;
			if (network.length > 0) {
				regionsInNetwork = new Set(network.map((n) => n.region));
				regions = regions.filter((r) => !regionsInNetwork.has(r.value));
			}
			setRegionOptions(regions);
		}
		stateFormHandler().size.onChange(account?.vpcSizeId);
	}, [account?.regionNames]);

	return (
		<StandardModal
			show={visible}
			onDismiss={() => setConnectivitySettingsModal(false)}
			title={
				isReadOnly
					? 'View Connectivity Configuration'
					: !isModalForNewRule()
						? 'Edit Connectivity Configuration'
						: 'New Connectivity Configuration'
			}
			size="em-c-modal--large"
			footer={
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
					{!isReadOnly ? (
						<>
							<Button name="Cancel" onClick={() => setConnectivitySettingsModal(false)} variant="secondary" />
							<Button
								name="Confirm"
								onClick={handleRequest}
								disabled={!isValid() || !isSubnetValid()}
								variant="primary"
							/>
						</>
					) : (
						<Button
							name="Close"
							onClick={() => setConnectivitySettingsModal(false)}
							variant="secondary"
						/>
					)}
				</div>
			}
		>
			<Grid variant="2up">
				<Grid.Item>
					<Select
						label="Region"
						{...stateFormHandler().region}
						options={regionOptions}
						disabled={isReadOnly}
						fullWidth
						note={''}
					/>
				</Grid.Item>
				<Grid.Item>
					<Select
						label="Priority"
						{...stateFormHandler().priority}
						options={[
							{ value: 1, text: 'Primary' },
						]}
						disabled={isReadOnly}
						fullWidth
						/>
					</Grid.Item>
				</Grid>
				<Grid variant="2up">
					<Grid.Item>
						<Select
							label="Size"
							{...stateFormHandler().size}
							options={[
								{ value: '1', text: 'Small' },
								{ value: '2', text: 'Medium' },
								{ value: '3', text: 'Large' },
							]}
							disabled={true}
							fullWidth
						/>
					</Grid.Item>
					<Grid.Item>
						<ToggleField
							id={'gateway-enabled'}
							label="S3 Gateway Endpoint enable"
							{...stateFormHandler().enableS3GatewayEndpoint}
							value={stateFormHandler().enableS3GatewayEndpoint.value?.toString()}
							options={[
								{ value: 'true', text: 'Enable' },
								{ value: 'false', text: 'Disable' },
							]}
							disabled={isReadOnly}
						/>
					</Grid.Item>
				</Grid>
				<Grid variant="2up">
					<Grid.Item>
						<TextInput label="VPC CIDR" {...stateFormHandler().cidr} disabled={isReadOnly} />
					</Grid.Item>
					<Grid.Item>
						<TextInput
							label="Route 53 Resolver Rule Associations"
							{...stateFormHandler().route53ResolverRuleAssociations}
							disabled={isReadOnly}
						/>
					</Grid.Item>
				</Grid>
				<Section title="Tags">
					<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
						{tags?.map((tag, index) => (
							<div key={index} style={{ 
								display: 'flex', 
								flexDirection: 'column', 
								gap: '0.5rem',
								padding: '1rem',
								backgroundColor: '#f8f9fa',
								borderRadius: '8px',
								border: '1px solid #e9ecef'
							}}>
								<Grid variant="2up">
									<Grid.Item>
										<TextInput
											label={`Key ${index + 1}`}
											value={tag.key}
											onChange={(e) => updateTag(index, 'key', e.target.value)}
											placeholder="Enter tag key"
											disabled={isReadOnly}
											validation={stateFormHandler().tags.validation}
										/>
									</Grid.Item>
									<Grid.Item>
										<TextInput
											label={`Value ${index + 1}`}
											value={tag.value}
											onChange={(e) => updateTag(index, 'value', e.target.value)}
											placeholder="Enter tag value"
											disabled={isReadOnly}
											validation={stateFormHandler().tags.validation}
										/>
									</Grid.Item>
								</Grid>
								{!isReadOnly && (
									<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
										<Button
											name="Remove Tag"
											onClick={() => removeTag(index)}
											variant="secondary"
											size="small"
											disabled={tags.length <= 1}
										/>
									</div>
								)}
							</div>
						))}
						{!isReadOnly && (
							<div style={{ display: 'flex', justifyContent: 'flex-start' }}>
								<Button
									name="Add Tag"
									onClick={addTag}
									variant="secondary"
									size="small"
								/>
							</div>
						)}
					</div>
				</Section>
				<Section title="Subnets">
					<Grid variant="2up">
						{state.stateForm.subnets.map((subnet, index) => (
							<Grid variant="2up" key={index}>
								<Grid.Item>
									<TextInput
										{...subnetStateFormHandler().name}
										value={subnet.name}
										onChange={(e) => updateSubnet(index, 'name', e.target.value)}
										id={`name-${index}`}
										label="Name"
										disabled={isReadOnly}
										validation={getSubnetValidation(subnet, 'name')}
									/>
								</Grid.Item>
								<Grid.Item>
									<TextInput
										{...subnetStateFormHandler().aZ1_CIDR}
										value={subnet.aZ1_CIDR}
										onChange={(e) => updateSubnet(index, 'aZ1_CIDR', e.target.value)}
										id={`aZ1_CIDR-${index}`}
										label="AZ1_CIDR"
										disabled={isReadOnly}
										validation={getSubnetValidation(subnet, 'aZ1_CIDR')}
									/>
								</Grid.Item>
								<Grid.Item>
									<TextInput
										{...subnetStateFormHandler().aZ2_CIDR}
										value={subnet.aZ2_CIDR ?? ''}
										onChange={(e) => updateSubnet(index, 'aZ2_CIDR', e.target.value)}
										id={`aZ2_CIDR-${index}`}
										label="AZ2_CIDR"
										disabled={isReadOnly}
										validation={getSubnetValidation(subnet, 'aZ2_CIDR')}
									/>
								</Grid.Item>
								<Grid.Item>
									<TextInput
										{...subnetStateFormHandler().aZ3_CIDR}
										value={subnet.aZ3_CIDR ?? ''}
										onChange={(e) => updateSubnet(index, 'aZ3_CIDR', e.target.value)}
										id={`aZ3_CIDR-${index}`}
										label="AZ3_CIDR"
										disabled={isReadOnly}
										validation={getSubnetValidation(subnet, 'aZ3_CIDR')}
									/>
								</Grid.Item>
								<Grid.Item>
									<ToggleField
										{...subnetStateFormHandler().uniqueRouteTablePerAz}
										value={subnet.uniqueRouteTablePerAz?.toString()}
										onChange={(e) => updateSubnet(index, 'uniqueRouteTablePerAz', e.target.value)}
										id={`uniqueRouteTablePerAz-${index}`}
										label="Unique Route Table Per Az"
										options={[
											{ value: 'true', text: 'Enable' },
											{ value: 'false', text: 'Disable' },
										]}
										disabled={isReadOnly}
									/>
								</Grid.Item>
								<Grid.Item>
									<div
										style={{
											display: 'flex',
											gap: '10px',
											alignItems: 'flex-end',
											alignSelf: 'center',
										}}
									>
										{!isReadOnly && (
											<Button
												name="Remove"
												onClick={() => removeSubnet(index)}
												type="secondary"
												disabled={state.stateForm.subnets.length <= 1}
												style={{
													marginTop: '18px',
												}}
											/>
										)}
									</div>
								</Grid.Item>
							</Grid>
						))}
						{!isReadOnly && (
							<>
								<Grid.Item />
								<Grid.Item />
								<Grid>
									<Button
										name="Add Subnet"
										onClick={addSubnets}
										type="secondary"
										optionalClass="btn-sm"
										style={{ marginTop: '10px' }}
									/>
								</Grid>
							</>
						)}
					</Grid>
				</Section>
				<Grid variant="2up"></Grid>
		</StandardModal>
	);
};

export default AccountConnectivitySettingsModal;
